import * as React from 'react';

import { createStyles, makeStyles } from '@material-ui/styles';
import * as classNames from 'classnames';
import isTouchDevice from 'is-touch-device';
import { useEffect, useRef, useState } from 'react';
import Flickity from 'react-flickity-component';
import mediaQueries from 'src/constants/responsive';
import spacing from 'src/constants/spacing';
import stylesheet, { secondaryColor } from 'src/constants/stylesheet';
import { Language } from 'src/utils/Languages';
import { getAlt, getLegend } from 'src/utils/legendAlt';
import previousCur from '../Assets/slider-left-white.cur';
import previousPng from '../Assets/slider-left-white.svg';
import nextCur from '../Assets/slider-right-white.cur';
import nextPng from '../Assets/slider-right-white.svg';
import Image from './Image';

const headerHeight = 70;

const useStyles = makeStyles(() =>
  createStyles({
    root: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'white',
      overflow: 'hidden',
      zIndex: 99999,
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      height: headerHeight,
      boxSizing: 'border-box',
      [mediaQueries.Mobile]: {
        paddingLeft: spacing.Gutter.Mobile,
        paddingRight: spacing.Gutter.Mobile,
      },
      [mediaQueries.Tablet]: {
        paddingLeft: spacing.Gutter.Tablet,
        paddingRight: spacing.Gutter.Tablet,
      },
      [mediaQueries.AfterTablet]: {
        paddingLeft: spacing.Gutter.Desktop,
        paddingRight: spacing.Gutter.Desktop
      },
    },
    title: {
      ...stylesheet.BoldText,
      color: secondaryColor
    },
    closeButton: {
      ...stylesheet.BoldText,
      fontSize: '1rem',
      border: 'none',
      backgroundColor: 'transparent',
      color: secondaryColor,
      cursor: 'pointer',

      '&:hover': {
        color: 'black'
      }
    },
    sliderContainer: {
      position: 'relative',
      width: '100%',
      height: `calc(100% - ${headerHeight}px)`,
      display: 'flex',
      flexDirection: 'column'
    },
    sliderInner: {
      flex: '1 1 auto',
      position: 'relative'
    },
    '@global': {
      '.lightbox-carousel': {
        flex: '1 1 auto',
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
        outline: 'none'
      },
      '.lightbox-carousel .flickity-viewport': {
        height: '100%'
      },
      '.lightbox-carousel .flickity-slider': {
        height: '100%'
      },
      '.lightbox-carousel .flickity-button': {
        background: 'transparent',
      },
      /* big previous & next buttons */
      '.lightbox-carousel .flickity-prev-next-button.previous': {
        position: 'absolute',
        width: '50%',
        top: 0,
        left: 0,
        height: '100%',
        zIndex: 1,
        padding: 0,
        border: 0,
        '&': {
          cursor: `url(${previousPng}),w-resize`,
        },
        cursor: `url(${previousCur}) 20 20 ,w-resize`,
      },
      '.lightbox-carousel .flickity-prev-next-button.next': {
        position: 'absolute',
        width: '50%',
        top: 0,
        right: 0,
        height: '100%',
        zIndex: 1,
        padding: 0,
        border: 0,
        '&': {
          cursor: `url(${nextPng}),e-resize`,
        },
        cursor: `url(${nextCur}) 20 20 ,e-resize`,
      },
      /* icon color */
      '.lightbox-carousel .flickity-button-icon': {
        display: 'none'
      },
    },
    legendContainer: {
      flex: '0 0 auto',
      width: '100%',
      height: headerHeight,
      boxSizing: 'border-box',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      [mediaQueries.Mobile]: {
        paddingLeft: spacing.Gutter.Mobile,
        paddingRight: spacing.Gutter.Mobile,
      },
      [mediaQueries.Tablet]: {
        paddingLeft: spacing.Gutter.Tablet,
        paddingRight: spacing.Gutter.Tablet,
      },
      [mediaQueries.AfterTablet]: {
        paddingLeft: spacing.Gutter.Desktop,
        paddingRight: spacing.Gutter.Desktop
      },
    },
    legend: {
      color: secondaryColor,
      display: 'none',
    },
    image: {
      width: '100%',
      height: '100%',
      objectFit: 'contain',
      objectPosition: 'center',
      ...stylesheet.ObjectFitContain
    },
    legendShow: {
      display: 'block'
    },
  }));

export type LightboxProps = {
  title: string;
  images: PictureRef[];
  initIndex: number;
  close: () => void;
};

const Lightbox: React.FC<LightboxProps> = ({ title, images, close, initIndex }) => {
  const cls = useStyles();

  const notTouch = !isTouchDevice();
  const flickity = useRef<any>(null);
  const [currentTouched, setCurrentTouched] = useState(initIndex);

  useEffect(() => {
    window.addEventListener('keydown', closeOnEscPress);
    return () => {
      window.removeEventListener('keydown', closeOnEscPress);
    };
  }, []);

  const closeOnEscPress = (e: KeyboardEvent) => {
    switch (e.keyCode) {
      case 27: // esc
        close()
        break;
      default:
        break;
    }
  }

  useEffect(
    () => {
      if (flickity.current) {
        flickity.current.on('change', (index: number) => {
          setCurrentTouched(index);
        });
      }
    },
    [flickity, setCurrentTouched]
  )

  const legends = images.map((image, i) =>
    <div
      key={`lightbox-legend-${i}`}
      className={classNames(cls.legend, currentTouched === i && cls.legendShow)}
      dangerouslySetInnerHTML={{ __html: getLegend(image) }}
    />);
  return (
    <section className={cls.root}>
      <header className={cls.header}>
        <h6 className={cls.title}>{title}</h6>
        <button
          className={cls.closeButton}
          onClick={close}
        >
          {Language === "fr" ? "Fermer" : "Close"}
        </button>
      </header>
      <div className={cls.sliderContainer}>
        <Flickity
          className={'lightbox-carousel'} // default ''
          // className={''} // default ''
          elementType={'div'} // default 'div'
          options={{
            pageDots: false,
            prevNextButtons: notTouch && images.length > 1,
            initialIndex: initIndex,
            wrapAround: true,
            setGallerySize: false,
            autoPlay: 6000
          }} // takes flickity options {}
          disableImagesLoaded={true} // default false
          // tslint:disable-next-line:jsx-no-lambda
          flickityRef={(r) => flickity.current = r}
        >
          {images.map((s, i) =>
            <Image
              key={i}
              className={cls.image}
              pictureRef={s}
              sizes="100vw"
              alt={getAlt(s)}
            />
          )}
        </Flickity>
        <div className={cls.legendContainer}>
          {legends}
        </div>
      </div>
    </section>
  );
}

export default Lightbox;



// WEBPACK FOOTER //
// ./src/Components/Lightbox.tsx